import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("monthly");
  const [seats, setSeats] = useState(30);
  const [loadingKey, setLoadingKey] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/pricing").then((r) => setPlans(r.data.plans)); }, []);

  const buy = async (plan) => {
    if (!user) { nav("/login"); return; }
    const lk = interval === "monthly" ? plan.monthly_lookup : plan.yearly_lookup;
    if (!lk) return;
    setLoadingKey(lk);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lk,
        quantity: plan.per_seat ? seats : 1,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoadingKey(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="nb-badge nb-tag-sun"><Sparkles className="w-3 h-3" /> Simple pricing</span>
        <h1 className="text-5xl font-extrabold mt-3">Learn free. Level up when you're ready.</h1>
        <p className="text-neutral-600 mt-2">Cancel anytime. Schools get seat-based licensing.</p>
      </div>

      <div className="mt-8 flex justify-center gap-2">
        <button data-testid="pricing-monthly" onClick={() => setInterval("monthly")} className={`px-5 py-2 rounded-full border-2 border-black font-bold text-sm ${interval === "monthly" ? "bg-[var(--coral)] text-white" : "bg-white"}`}>Monthly</button>
        <button data-testid="pricing-yearly" onClick={() => setInterval("yearly")} className={`px-5 py-2 rounded-full border-2 border-black font-bold text-sm ${interval === "yearly" ? "bg-[var(--coral)] text-white" : "bg-white"}`}>Yearly (save 20%)</button>
      </div>

      <div className="mt-10 grid md:grid-cols-4 gap-5">
        {plans.map((p) => {
          const price = interval === "monthly" ? p.price_monthly : (p.price_yearly ?? p.price_monthly * 12 * 0.8);
          const lk = interval === "monthly" ? p.monthly_lookup : p.yearly_lookup;
          const isFree = p.id === "free";
          const isSchool = p.per_seat;
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`nb-card p-6 ${p.highlight ? "bg-[var(--sun)]" : "bg-white"}`}>
              {p.highlight && <div className="nb-badge nb-tag-coral">Most popular</div>}
              <h3 className="text-2xl font-extrabold mt-2">{p.name}</h3>
              <div className="mt-2 flex items-end gap-1">
                <span className="text-4xl font-extrabold">${price}</span>
                <span className="text-sm text-neutral-600">/{interval === "monthly" ? "mo" : "yr"}{isSchool ? " · seat" : ""}</span>
              </div>
              {isSchool && (
                <div className="mt-3">
                  <label className="text-xs font-bold uppercase">Seats</label>
                  <input data-testid={`plan-${p.id}-seats`} type="number" min={1} max={2000} value={seats} onChange={(e) => setSeats(Math.max(1, Number(e.target.value) || 1))} className="nb-input mt-1" />
                  <div className="mt-1 text-sm text-neutral-600">Total: <b>${(price * seats).toFixed(2)}</b>/{interval === "monthly" ? "mo" : "yr"}</div>
                </div>
              )}
              <ul className="mt-4 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 items-start"><Check className="w-4 h-4 mt-0.5 text-[var(--coral)]" /> {f}</li>
                ))}
              </ul>
              <button
                data-testid={`plan-${p.id}-cta`}
                disabled={isFree || !lk || loadingKey === lk}
                onClick={() => buy(p)}
                className={`mt-5 w-full nb-btn ${isFree ? "nb-btn-secondary" : ""}`}
              >
                {isFree ? "Current" : loadingKey === lk ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : "Choose plan"}
              </button>
            </div>
          );
        })}
      </div>
      <div className="text-center text-sm text-neutral-500 mt-10">
        Payments powered by Stripe. Test card <code className="px-1 bg-white border border-black rounded">4242 4242 4242 4242</code>.
      </div>
    </div>
  );
}
